import {
  Mjml,
  MjmlAll,
  MjmlAttributes,
  MjmlBody,
  MjmlBreakpoint,
  MjmlHead,
  MjmlPreview,
  MjmlRaw,
} from "@faire/mjml-react";
import { ElementVisibility } from "@redotech/redo-content-builder-common/element-schema";
import { quillStyles } from "@redotech/redo-content-builder-common/quill-styles";
import {
  EMAIL_MAX_WIDTH_PX,
  EMAIL_MOBILE_BREAKPOINT_WIDTH_PX,
} from "@redotech/redo-marketing-core-common/email-builder/email-sizing";
import {
  EmailFormat,
  ObjectFit,
} from "@redotech/redo-marketing-templates-common/email-builder";
import { cloneElement, ReactElement, ReactNode, useContext } from "react";
import { EmailRenderContext } from "./builder/email-render-context";
import { Abbrv } from "./utils/compression";

/**
 * Characters used to pad preview text to prevent email clients from showing body content.
 * Non-breaking space + Combining Grapheme Joiner + Soft Hyphen.
 * See https://www.goodemailcode.com/email-code/preheader
 */
const PREVIEW_PADDING_CHARS = "\u00A0\u200C\u00AD";
const PREVIEW_PADDING_REPEAT_COUNT = 120;

export enum GlobalEmailClass {
  MJML_BODY = "mjml-body",
  MJML_PREVIEW_BODY = "mjml-preview-body",
  MJML_PREVIEW_STYLE_TAG = "mjml-preview-style-tag",
  SPINNER = "spinner",
  LINE_ITEM_LOADER = "line-item-loader",
  SUBMITTING_OVERLAY = "submitting-overlay",
  APPLE_MAIL_ONLY = "apple-mail-show",
  APPLE_MAIL_HIDE = "apple-mail-hide",
  FORCE_FALLBACK = "force-fallback",
  OBJECT_FIT_CONTAIN = "contain",
  OBJECT_FIT_COVER = "cover",
  STACK_ON_MOBILE = "stack-on-mobile",
  OVERFLOW_X_AUTO = "o-x-auto",
  HIDE_ON_MOBILE = "redo-hide-on-mobile",
  HIDE_ON_DESKTOP = "redo-hide-on-desktop",
}

export function displayVisibilityClass(
  visibility: ElementVisibility | null | undefined,
): GlobalEmailClass | undefined {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (visibility) {
    case ElementVisibility.DESKTOP_ONLY:
      return GlobalEmailClass.HIDE_ON_MOBILE;
    case ElementVisibility.MOBILE_ONLY:
      return GlobalEmailClass.HIDE_ON_DESKTOP;
    default:
      return undefined;
  }
}

export const objectFitToClass = {
  [ObjectFit.COVER]: GlobalEmailClass.OBJECT_FIT_COVER,
  [ObjectFit.CONTAIN]: GlobalEmailClass.OBJECT_FIT_CONTAIN,
} as const;

export function MjmlBase(props: {
  headContents: ReactNode;
  bodyClasses: string[];
  preview?: string;
  backgroundColor?: string;
  registeredBodyElements: ReactElement[];
  format: EmailFormat;
  children: ReactNode;
  width?: number;
}) {
  const renderContext = useContext(EmailRenderContext);

  return (
    // AMP attributes on the root html element are added in a post-processing step after MJML rendering
    <Mjml>
      <MjmlHead>
        <MjmlBreakpoint width={`${EMAIL_MOBILE_BREAKPOINT_WIDTH_PX}px`} />
        <MjmlRaw>
          <meta charSet="utf-8" />
          {props.headContents}
          {renderContext?.format === EmailFormat.AMP && (
            <style amp4email-boilerplate="">{"body{visibility:hidden}"}</style>
          )}
        </MjmlRaw>
        <MjmlAttributes>
          <MjmlAll padding="0" />
        </MjmlAttributes>
        {props.preview && (
          <MjmlPreview>
            {props.preview}
            {PREVIEW_PADDING_CHARS.repeat(PREVIEW_PADDING_REPEAT_COUNT)}
          </MjmlPreview>
        )}
      </MjmlHead>
      <MjmlBody
        backgroundColor={props.backgroundColor}
        cssClass={props.bodyClasses.join(" ")}
        width={props.width ?? EMAIL_MAX_WIDTH_PX}
      >
        <MjmlRaw>
          {props.registeredBodyElements.map((e) =>
            cloneElement(e, { key: e.key }),
          )}
        </MjmlRaw>
        {props.children}
      </MjmlBody>
    </Mjml>
  );
}

export const MjmlBaseProduction = ({
  customCss,
  registeredBodyElements,
  registeredHeadElements,
  format,
  emailBody,
  children,
  preview,
  backgroundColor,
  forceFallback,
}: {
  customCss?: string[];
  registeredBodyElements: ReactElement[];
  registeredHeadElements: ReactElement[];
  format: EmailFormat;
  emailBody: string;
  children: React.ReactNode;
  forceFallback?: boolean;
  preview: string | null;
  backgroundColor: string;
}) => {
  const bodyClasses = [GlobalEmailClass.MJML_BODY];
  if (forceFallback) {
    bodyClasses.push(GlobalEmailClass.FORCE_FALLBACK);
  }

  const styleTags = [
    ...(customCss ?? []),
    ...MERCHANT_TEMPLATED_EMAIL_GLOBAL_STYLES,
  ];

  return (
    <MjmlBase
      backgroundColor={backgroundColor}
      bodyClasses={bodyClasses}
      format={format}
      headContents={
        <>
          {format === EmailFormat.AMP &&
            necessaryAmpScripts(emailBody).map((script) => (
              <AmpScriptTag key={script.src} script={script} />
            ))}
          {styleTags?.map((css) => (
            // Our AMP formatter is smart enough to combine all of these into a single <style amp-custom=""> tag
            <style dangerouslySetInnerHTML={{ __html: css }} key={css} />
          ))}
          {registeredHeadElements}
        </>
      }
      preview={preview ?? undefined}
      registeredBodyElements={registeredBodyElements}
    >
      {children}
    </MjmlBase>
  );
};

export interface AmpScript {
  src: string;
  customElement?: string;
  customElements?: string[];
  customTemplate?: string;
  includeIfStringPresent?: string;
}

export const AmpScriptTag = ({ script }: { script: AmpScript }) => (
  <script
    async
    custom-element={script.customElement}
    custom-template={script.customTemplate}
    key={script.src}
    src={script.src}
  />
);

export const ampScripts: AmpScript[] = [
  { src: "https://cdn.ampproject.org/v0.js", customElement: undefined },
  {
    src: "https://cdn.ampproject.org/v0/amp-form-0.1.js",
    customElement: "amp-form",
    includeIfStringPresent: "<form",
  },
  {
    src: "https://cdn.ampproject.org/v0/amp-list-0.1.js",
    customElement: "amp-list",
  },
  {
    src: "https://cdn.ampproject.org/v0/amp-mustache-0.2.js",
    customTemplate: "amp-mustache",
    includeIfStringPresent: "<template",
  },
  {
    src: "https://cdn.ampproject.org/v0/amp-bind-0.1.js",
    customElement: "amp-bind",
    includeIfStringPresent: "<amp-state",
  },
];

function necessaryAmpScripts(emailBody: string) {
  return ampScripts.filter((script) => {
    if (script.src === "https://cdn.ampproject.org/v0.js") {
      return true;
    }
    if (
      script.customElement &&
      emailBody.includes("<" + script.customElement)
    ) {
      return true;
    }
    if (script.customTemplate && emailBody.includes(script.customTemplate)) {
      return true;
    }
    if (
      script.includeIfStringPresent &&
      emailBody.includes(script.includeIfStringPresent)
    ) {
      return true;
    }
    return false;
  });
}

/**
 * If you change this, please also update SELECT_HEIGHT_PX to match the new height of the element.
 */
const selectStyles = `
    select, .${Abbrv.SELECT} {
      background-color: transparent;
      border: 1px solid #d6d6d6;
      border-radius: 6px;
      padding: 6px 8px 6px 8px;
      display: block;
      margin-top: 8px;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      background-image: url(https://assets.getredo.com/chevron.png);
      background-repeat: no-repeat;
      background-position: right 10px top 50%;
      background-size: 12px;
      font-size: 12px;
      line-height: 16px;
      color: inherit;
    }
`;

/**
 * Without these styles, email clients will try to render both the interactive and static version of the email,
 * and interactive components will look absolutely atrocious.
 */
export const ABSOLUTELY_ESSENTIAL_STYLES = `.${GlobalEmailClass.APPLE_MAIL_ONLY} {
      display: none !important;
    }
    /** CSS counter support is a good proxy for whether we're in a modern email client */
    @supports (counter-reset: x) {
      .${GlobalEmailClass.MJML_BODY}:not(.${GlobalEmailClass.FORCE_FALLBACK}) .${GlobalEmailClass.APPLE_MAIL_HIDE} {
        display: none !important;
      }
      .${GlobalEmailClass.MJML_BODY}:not(.${GlobalEmailClass.FORCE_FALLBACK}) .${GlobalEmailClass.APPLE_MAIL_ONLY} {
        ${/* Must have !important to have enough specificity to override style="display:none;"*/ ""}
        display: block !important;
      }
    }`;

export const SECOND_HIGHEST_PRIORITY_STYLES = `
    .hidden, .${Abbrv.HIDDEN} {
      display: none;
    }
    .${Abbrv.HIDDEN_FOCUSABLE} {
      position: absolute;
      height: 0;
      width: 0;
      opacity: 0;
    }
`;

export const PER_ELEMENT_VISIBILITY_STYLES = `
    .${GlobalEmailClass.HIDE_ON_MOBILE} {
      display: none !important;
    }
    @media only screen and (min-width: ${EMAIL_MOBILE_BREAKPOINT_WIDTH_PX}px) {
      .${GlobalEmailClass.HIDE_ON_MOBILE} {
        display: block !important;
      }
      .${GlobalEmailClass.HIDE_ON_DESKTOP} {
        display: none !important;
      }
    }
`;

/**
 * The global styles for the merchant templated email.
 *
 * Each element in the array gets its own <style> tag in the <head> of the email.
 * The reason for an array is that outlook.com will strip the entire <style> tag if it
 * contains anything it doesn't like, so we split out the styles we can live without from the
 * ones we absolutely need no matter what.
 *
 * Side note: outlook.com's CSS parser is a total mess. It tries to prefix all class names with "x_",
 * but it will miss edge cases. For example, .class1.class2 will become .x_class1.class2, which won't match anything.
 * So be careful with class names.
 */
/** Minimal styles for plain text emails. */
export const PLAIN_TEXT_EMAIL_STYLES = ["* { margin: 0; }"];

/**
 * A simple email wrapper that bypasses MJML entirely for plain text emails.
 * Renders basic HTML structure that can go directly through renderToString.
 */
export const PlainTextEmailWrapper = ({
  children,
  preview,
  backgroundColor,
}: {
  children: React.ReactNode;
  preview: string | null;
  backgroundColor: string;
}) => {
  return (
    <html>
      <head>
        <meta charSet="utf-8" />
        <style
          dangerouslySetInnerHTML={{ __html: PLAIN_TEXT_EMAIL_STYLES.join("") }}
        />
      </head>
      <body style={{ backgroundColor, margin: 0, padding: 0 }}>
        {preview && (
          <div style={{ display: "none", maxHeight: 0, overflow: "hidden" }}>
            {preview}
            {PREVIEW_PADDING_CHARS.repeat(PREVIEW_PADDING_REPEAT_COUNT)}
          </div>
        )}
        {children}
      </body>
    </html>
  );
};

export const MERCHANT_TEMPLATED_EMAIL_GLOBAL_STYLES = [
  ABSOLUTELY_ESSENTIAL_STYLES,
  SECOND_HIGHEST_PRIORITY_STYLES,
  PER_ELEMENT_VISIBILITY_STYLES,
  `
    * {
      margin: 0;
    }
    html, body {
      width: 100%;
      margin: 0;
      padding: 0;
    }
    p {
      margin: 0;
    }
    body {
      padding-top: 76px;
      padding-bottom: 76px;
    }
    @media screen and (max-width: 600px) {
      body {
        padding: 0;
      }
      .${GlobalEmailClass.MJML_BODY} {
        width: 100%;
      }
    }

    /* 🔧 FIX: Override MJML's 420px breakpoint for menu sections with stackOnMobile=false */
    /* MJML forces columns to stack below 420px, but we need to prevent this when explicitly disabled */
    /* Target the group wrapper and column divs directly without attribute selectors */
    @media only screen and (max-width: 419px) {
      .menu-no-stack .mj-column-per-100 div[class*=mj-column-per-] {
        display: inline-block !important;
        vertical-align: top !important;
      }
      .menu-no-stack div.mj-column-per-50 {
        display: inline-block !important;
        vertical-align: top !important;
        width: 50% !important;
      }
      .menu-no-stack div[class^=mj-column-per-] {
        display: inline-block !important;
        vertical-align: top !important;
      }
    }

    .${GlobalEmailClass.OVERFLOW_X_AUTO} {
      overflow-x: auto;
    }

    ${quillStyles}

    amp-img.${GlobalEmailClass.OBJECT_FIT_CONTAIN} img {
      object-fit: contain;
    }
    amp-img.${GlobalEmailClass.OBJECT_FIT_COVER} img {
      object-fit: cover;
    }
    .left img {
      object-position: left;
    }
    .right img {
      object-position: right;
    }
    .center img {
      object-position: center;
    }
    ${selectStyles}
    select:focus, .${Abbrv.SELECT}:focus {
      outline: 1px solid #999;
    }
    .${GlobalEmailClass.LINE_ITEM_LOADER} {
      z-index: 99; /* 99 is max value in AMP */
      visibility: hidden;
    }
    .amp-form-submitting .${GlobalEmailClass.LINE_ITEM_LOADER}, .amp-form-submit-success .${GlobalEmailClass.LINE_ITEM_LOADER} {
      visibility: visible;
    }
    .${GlobalEmailClass.SUBMITTING_OVERLAY} {
      visibility: hidden;
    }
    .amp-form-submitting .${GlobalEmailClass.SUBMITTING_OVERLAY} {
      visibility: visible;
    }
    .${GlobalEmailClass.SPINNER} {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 2px solid transparent;
      border-left-color: #000;
      transform: rotate(0deg);
    }
    /* These animations cannot be put on the spinner at page load for some strange reason */
    .amp-form-submitting .${GlobalEmailClass.SPINNER}, .amp-form-submit-success .${GlobalEmailClass.SPINNER} {
      /* animations and keyframes are not supported in AMP */
      transition: transform 10000s linear;
      transform: rotate(3600000deg);
    }
  `,
];
